import type { DocumentTemplateVersionDetailPayload } from '@Application/Documents/Templates/Actions/documentTemplateVersionFetched';
import DeclarationGroupView from '@UI/Pages/DocumentTemplates/Shared/Declaration/DeclarationGroupView';
import DeclaredFieldView from '@UI/Store/DocumentTemplates/Views/Document/DeclaredFieldView';
import DeclaredNodeView from '@UI/Store/DocumentTemplates/Views/Document/DeclaredNodeView';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * `templateId` is read off the `template` relation (`{ id }`). `nodes`/`fields` may both be empty
 * arrays — a version can declare zero variables without being rejected.
 */
class DocumentTemplateVersionDetailView {
	readonly id: number;
	readonly templateId: number;
	readonly versionNumber: number;
	readonly originalFilename: string;
	readonly fieldCount: number;
	readonly createdAt: string;
	readonly reevaluationSectionKeys: readonly string[];
	readonly nodes: readonly DeclaredNodeView[];
	readonly fields: readonly DeclaredFieldView[];

	constructor(payload: DocumentTemplateVersionDetailPayload) {
		this.id = payload.id;
		this.templateId = payload.template.id;
		this.versionNumber = payload.versionNumber;
		this.originalFilename = payload.originalFilename;
		this.fieldCount = payload.fieldCount;
		this.createdAt = payload.createdAt;
		this.reevaluationSectionKeys = [...payload.reevaluationSectionKeys];
		this.nodes = payload.declaration.nodes.map((node) => new DeclaredNodeView(node));
		this.fields = payload.declaration.fields.map((field) => new DeclaredFieldView(field));

		Object.freeze(this);
	}

	/** The declaration as a tree of sections: root variables first, then first-level groups. */
	get sections(): readonly DeclarationGroupView[] {
		return DeclarationGroupView.buildSections(this.nodes, this.fields);
	}

	isReevaluationSection(uuid: string): boolean {
		return this.reevaluationSectionKeys.includes(uuid);
	}
}

export default DocumentTemplateVersionDetailView;
